import React, { useId, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { PencilLine, Search } from 'lucide-react';
import Button from '../../ui/Button';
import Checkbox from '../../ui/Checkbox';
import IconButton from '../../ui/IconButton';
import Popover from '../../ui/Popover';
import TextInput from '../../ui/TextInput';
import Tooltip from '../../ui/Tooltip';
import { cx } from '../../ui/classNames';
import { watchlistSearchTerms } from '../watchlistRowSearch';
import CategoryPill from './CategoryPill';
import {
    BLANK_CATEGORY_LABEL,
    categoryFilterOptionKeys,
    clearAllCategories,
    isCategoryFilterActive,
    isCategorySelected,
    selectAllCategories,
    setCategoriesSelected,
    toggleCategoryKey,
} from './categoryFilter';
import { categoryFacets } from './categoryTools';

const OPTION_SELECTOR = '[data-category-option]';

// MUI's hover overlay (action.hover) for list rows and quiet buttons.
const HOVER_TINT = 'hover:bg-black/[0.04] dark:hover:bg-white/[0.08]';
// The title: MUI's subtitle2 size (smaller in compact mode), bold.
const TITLE_CLASS_NAME = 'm-0 truncate text-[14px] font-bold leading-[1.3] [[data-compact]_&]:text-[12px]';
// Small text buttons: tighter than the kit's control size.
const ACTION_BUTTON_CLASS_NAME = '!min-h-7 !px-2 !py-0.5';
const LIST_CLASS_NAME = 'm-0 max-h-[300px] list-none overflow-y-auto px-1 pb-1.5 pt-0';
const OPTION_CLASS_NAME = `flex min-h-8 items-center gap-0.5 rounded-md pr-1 ${HOVER_TINT}`;
// Quiet next to the checkbox: no frame until hovered (the kit IconButton's
// frame is too heavy for a list row).
const RENAME_BUTTON_CLASS_NAME = cx(
    '!h-[26px] !w-[26px] !border-transparent !bg-transparent !text-content-secondary',
    'hover:!border-hairline hover:!bg-black/[0.04] dark:hover:!bg-white/[0.08]',
);
// MUI's body2 in secondary text, for the empty states.
const EMPTY_CLASS_NAME = 'm-0 px-3 pb-3 text-[14px] leading-[1.43] text-content-secondary [[data-compact]_&]:text-[12px]';

function matchesSearch(label, terms) {
    const text = watchlistSearchTerms(label).join(' ');
    return terms.every((term) => text.includes(term));
}

// Mounted only while the popover is open, so the search starts empty each time.
function CategoryFilterContent({
    titleId,
    columnLabel,
    values,
    allValues,
    filter,
    onChange,
    onRenameRequest,
}) {
    const [query, setQuery] = useState('');
    const searchRef = useRef(null);
    const listRef = useRef(null);
    const facets = useMemo(() => categoryFacets(values), [values]);
    const allKeys = useMemo(
        () => categoryFilterOptionKeys(allValues ? categoryFacets(allValues) : facets),
        [allValues, facets],
    );

    const terms = watchlistSearchTerms(query);
    const searching = terms.length > 0;
    const options = [
        ...facets.categories.map((category) => ({
            key: category.key,
            label: category.value,
            count: category.count,
            category,
        })),
        ...(facets.blankCount > 0
            ? [{ key: '', label: BLANK_CATEGORY_LABEL, count: facets.blankCount, category: null }]
            : []),
    ].filter((option) => matchesSearch(option.label, terms));
    const visibleKeys = options.map((option) => option.key);
    const tickedCount = options.filter((option) => isCategorySelected(filter, option.key)).length;

    // Like Excel, Select all and Clear act on the search matches while searching.
    const selectShown = () => onChange(searching
        ? setCategoriesSelected(filter, visibleKeys, true, allKeys)
        : selectAllCategories());
    const clearShown = () => onChange(searching
        ? setCategoriesSelected(filter, visibleKeys, false, allKeys)
        : clearAllCategories());

    const focusOption = (index) => {
        if (index < 0) {
            searchRef.current?.focus();
            return;
        }
        const rows = listRef.current?.querySelectorAll(OPTION_SELECTOR) || [];
        rows[Math.min(index, rows.length - 1)]?.querySelector('input')?.focus();
    };
    const handleSearchKeyDown = (event) => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        focusOption(0);
    };
    const handleListKeyDown = (event) => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        const rows = [...listRef.current.querySelectorAll(OPTION_SELECTOR)];
        const index = rows.indexOf(event.target.closest(OPTION_SELECTOR));
        if (index < 0) return;
        event.preventDefault();
        focusOption(index + (event.key === 'ArrowDown' ? 1 : -1));
    };

    return (
        <>
            <div className="px-3 pb-1.5 pt-2.5">
                <h2 id={titleId} className={TITLE_CLASS_NAME}>
                    Filter {columnLabel}
                </h2>
                <TextInput
                    autoFocus
                    ref={searchRef}
                    value={query}
                    placeholder="Search"
                    aria-label={`Search ${columnLabel} categories`}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    className="mt-2 !pl-[13px] !pr-2"
                    startAdornment={(
                        <span className="mr-[15px] inline-flex text-black/[0.54] dark:text-white">
                            <Search aria-hidden="true" size={20} />
                        </span>
                    )}
                />
                <div className="mt-1.5 flex gap-1">
                    <Button
                        className={ACTION_BUTTON_CLASS_NAME}
                        disabled={searching ? tickedCount === options.length : !isCategoryFilterActive(filter)}
                        onClick={selectShown}
                    >
                        {searching ? 'Select matches' : 'Select all'}
                    </Button>
                    <Button className={ACTION_BUTTON_CLASS_NAME} disabled={tickedCount === 0} onClick={clearShown}>
                        {searching ? 'Clear matches' : 'Clear'}
                    </Button>
                </div>
            </div>
            {options.length > 0 ? (
                <ul
                    ref={listRef}
                    aria-label={`${columnLabel} categories`}
                    onKeyDown={handleListKeyDown}
                    className={LIST_CLASS_NAME}
                >
                    {options.map((option, index) => {
                        const countId = `${titleId}-count-${index}`;
                        return (
                            <li
                                key={option.category ? `category:${option.key}` : 'blank'}
                                data-category-option
                                className={OPTION_CLASS_NAME}
                            >
                                <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-1.5">
                                    <Checkbox
                                        checked={isCategorySelected(filter, option.key)}
                                        onChange={() => onChange(toggleCategoryKey(filter, option.key, allKeys))}
                                        aria-label={option.label}
                                        aria-describedby={countId}
                                        className="!h-7 !w-7"
                                    />
                                    {option.category
                                        ? <CategoryPill value={option.label} />
                                        : (
                                            <span className="text-[12px] italic text-content-secondary">
                                                {option.label}
                                            </span>
                                        )}
                                    <span
                                        id={countId}
                                        className="ml-auto shrink-0 text-[11.5px] tabular-nums text-content-secondary"
                                    >
                                        {option.count.toLocaleString()}
                                    </span>
                                </label>
                                {onRenameRequest && (option.category ? (
                                    <Tooltip content="Rename…">
                                        <IconButton
                                            label={`Rename ${option.label}`}
                                            size="sm"
                                            className={RENAME_BUTTON_CLASS_NAME}
                                            onClick={() => onRenameRequest(option.category)}
                                        >
                                            <PencilLine aria-hidden="true" size={16} />
                                        </IconButton>
                                    </Tooltip>
                                ) : <span className="w-[26px] shrink-0" />)}
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <p className={EMPTY_CLASS_NAME}>
                    {searching ? 'No matching categories' : 'No values in this column'}
                </p>
            )}
        </>
    );
}

/**
 * Excel-style value filter for one category column, below its anchor (the
 * column's filter button or a toolbar chip). It holds focus while open, as a
 * modal popover, and stays open under the Rename dialog it opens. Ticking or
 * unticking an option calls onChange(nextFilter) at once; Escape or a click
 * outside calls onClose. `values` are the saved values of the rows in scope and supply the
 * list and its counts; `allValues` (default `values`) are every saved value in
 * the column, so unticking one option never hides categories that other
 * filters keep out of view.
 */
export default function CategoryFilterPopover({
    open,
    anchorEl,
    columnLabel,
    values,
    allValues,
    filter,
    onChange,
    onClose,
    onRenameRequest,
}) {
    const titleId = useId();
    return (
        <Popover
            anchor={anchorEl}
            open={Boolean(open && anchorEl)}
            onOpenChange={(next) => {
                if (!next) onClose();
            }}
            offset={0}
            modal
            aria-labelledby={titleId}
            className="flex max-h-[calc(100vh-32px)] w-[296px] max-w-[calc(100vw-24px)] flex-col overflow-y-auto overflow-x-hidden"
        >
            <CategoryFilterContent
                titleId={titleId}
                columnLabel={columnLabel}
                values={values}
                allValues={allValues}
                filter={filter}
                onChange={onChange}
                onRenameRequest={onRenameRequest}
            />
        </Popover>
    );
}

const filterShape = PropTypes.shape({
    keys: PropTypes.arrayOf(PropTypes.string),
    blank: PropTypes.bool,
});

CategoryFilterContent.propTypes = {
    titleId: PropTypes.string.isRequired,
    columnLabel: PropTypes.string.isRequired,
    values: PropTypes.array.isRequired,
    allValues: PropTypes.array,
    filter: filterShape,
    onChange: PropTypes.func.isRequired,
    onRenameRequest: PropTypes.func,
};

CategoryFilterPopover.propTypes = {
    open: PropTypes.bool.isRequired,
    anchorEl: PropTypes.object,
    columnLabel: PropTypes.string.isRequired,
    values: PropTypes.array.isRequired,
    allValues: PropTypes.array,
    filter: filterShape,
    onChange: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onRenameRequest: PropTypes.func,
};
